// Drawing Guess in VR — everything works with bare hands:
//  - not joined: the Join panel in front of the easel;
//  - joined: a panel that follows you around the room (lobby + options, the
//    word choice, the guess pad with a big on-panel keyboard, the reveal and
//    the final scores). A Bluetooth / physical keyboard types into it too;
//  - your turn to draw: the drawing pad (VRDrawPad) in front of you.
// Table-local, floor origin.

import React, { useEffect, useRef, useState } from 'react';
import { Text } from '@react-three/drei';
import { tableAction } from '../gameStore';
import { leaveTable, useCountdown } from '../kit/LobbyKit';
import { VRBtn, VRJoinPanel, VRRulesPanel, VRLeaderboardPanel, FloatingPanelMount, Surface, noRaycast, vrColors as colors } from '../kit/vrKit';
import { VRDrawPad } from './VRDrawPad';
import { chatLineText, hintText, myStatus, nameAt, phaseDeadline, standings, type DrawPriv, type DrawState } from './drawingView';
import { DEFAULT_OPTIONS, DIFFICULTIES, DRAW_SECONDS, GUESS_MAX_LEN, ROUND_OPTIONS, type DrawOptions } from '../../../../supabase/functions/games/drawing.ts';
import type { VRPanelProps } from '../types';

const W = 0.7;
const ROWS = ['QWERTYUIOP', 'ASDFGHJKL', 'ZXCVBNM'];
const KEY_W = 0.058, KEY_H = 0.056, KEY_GAP = 0.006;

/** Big-key keyboard for guesses: letters, space, backspace, Send. */
function GuessKeyboard({ y, onKey, onBack, onSend, canSend }: { y: number; onKey: (c: string) => void; onBack: () => void; onSend: () => void; canSend: boolean }) {
  const step = KEY_W + KEY_GAP;
  return (
    <group position={[0, y, 0]}>
      {ROWS.map((row, r) => row.split('').map((ch, i) => (
        <VRBtn key={ch} name={`vr-draw-key-${ch}`} label={ch} fontSize={0.022}
          x={-((row.length - 1) * step) / 2 + i * step} y={-r * (KEY_H + KEY_GAP)} w={KEY_W} h={KEY_H} onPress={() => onKey(ch.toLowerCase())} />
      )))}
      <VRBtn name="vr-draw-key-back" label="⌫" x={-0.215} y={-3 * (KEY_H + KEY_GAP)} w={0.15} h={KEY_H} fontSize={0.022} onPress={onBack} />
      <VRBtn name="vr-draw-key-space" label="space" x={-0.02} y={-3 * (KEY_H + KEY_GAP)} w={0.22} h={KEY_H} fontSize={0.016} onPress={() => onKey(' ')} />
      <VRBtn name="vr-draw-key-send" label="Send" x={0.2} y={-3 * (KEY_H + KEY_GAP)} w={0.15} h={KEY_H} fontSize={0.018} active={canSend} disabled={!canSend} onPress={onSend} />
    </group>
  );
}

function Countdown({ deadline, x, y, size = 0.02 }: { deadline: number | null; x: number; y: number; size?: number }) {
  const left = useCountdown(deadline);
  if (left === null) return null;
  return <Text raycast={noRaycast} position={[x, y, 0.004]} anchorX="right" fontSize={size} color={left <= 10 ? '#ff9b8a' : colors.text}>{`${left}s`}</Text>;
}

function OptionButtons<T extends string | number>({ label, y, values, current, fmt, onPick, disabled }: {
  label: string; y: number; values: readonly T[]; current: T; fmt: (v: T) => string; onPick: (v: T) => void; disabled: boolean;
}) {
  const bw = values.length > 3 ? 0.11 : 0.13;
  return <>
    <Text raycast={noRaycast} position={[-W / 2 + 0.03, y, 0.004]} anchorX="left" fontSize={0.016} color={colors.muted}>{label}</Text>
    {values.map((v, i) => (
      <VRBtn key={String(v)} name={`vr-draw-opt-${label}-${v}`} label={fmt(v)} x={-0.12 + bw / 2 + i * (bw + 0.008)} y={y} w={bw} h={0.052} fontSize={0.015}
        active={v === current} disabled={disabled} onPress={() => v !== current && onPick(v)} />
    ))}
  </>;
}

export function DrawingVRPanel({ def, pid, worldId, entry, table, seat }: VRPanelProps) {
  const [rules, setRules] = useState(false);
  const [board, setBoard] = useState(false);
  const [typed, setTyped] = useState('');
  const [close, setClose] = useState<string | null>(null);
  const st = table.state;
  const g = (st.g ?? null) as DrawState | null;
  const priv = (entry.priv ?? null) as DrawPriv | null;
  const playing = st.phase === 'playing' && !!g;
  const amDrawer = playing && g!.drawer === seat;
  const canGuess = playing && g!.phase === 'drawing' && !amDrawer && !priv?.guessed && seat >= 0;
  const turnKey = g ? `${st.gameId}:${g.turnNo}` : '';
  useEffect(() => { setTyped(''); setClose(null); }, [turnKey]);
  const closeN = priv?.closeN ?? 0;
  useEffect(() => {
    if (!closeN || !priv?.close) return;
    setClose(priv.close);
    const t = window.setTimeout(() => setClose(null), 2600);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeN]);

  const send = () => {
    const t = typed.trim();
    if (!t) return;
    setTyped('');
    void tableAction(pid, 'move', { move: { type: 'guess', text: t } });
  };
  const sendRef = useRef(send);
  sendRef.current = send;
  // A physical (Bluetooth) keyboard types into the guess too.
  useEffect(() => {
    if (!canGuess) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') { e.preventDefault(); sendRef.current(); return; }
      if (e.key === 'Backspace') { setTyped((v) => v.slice(0, -1)); return; }
      if (e.key.length === 1 && /[\p{L}\p{N} '\-]/u.test(e.key)) setTyped((v) => (v + e.key).slice(0, GUESS_MAX_LEN));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canGuess]);

  // Not joined: Join / Host / rules / leaders, in front of the easel.
  if (seat < 0) {
    return <group position={[0, 0, 1.3]}><VRJoinPanel def={def} pid={pid} entry={entry} worldId={worldId} y={1.3} /></group>;
  }

  const topBar = (top: number) => <>
    <VRBtn label="Leave" x={W / 2 - 0.07} y={top - 0.035} w={0.11} h={0.045} fontSize={0.014} onPress={() => leaveTable(pid)} />
    <VRBtn name="vr-game-howto" label="?" x={W / 2 - 0.16} y={top - 0.035} w={0.05} h={0.045} fontSize={0.016} active={rules} onPress={() => { setBoard(false); setRules((v) => !v); }} />
    <VRBtn name="vr-game-leaders" label="Leaders" x={W / 2 - 0.26} y={top - 0.035} w={0.12} h={0.045} fontSize={0.014} active={board} onPress={() => { setRules(false); setBoard((v) => !v); }} />
  </>;
  const overlays = <>
    {rules && <group position={[0, 0, 0.03]}><VRRulesPanel def={def} y={-0.31} onClose={() => setRules(false)} /></group>}
    {board && <group position={[0, 0, 0.03]}><VRLeaderboardPanel def={def} worldId={worldId} refreshKey={st.gameId} onClose={() => setBoard(false)} /></group>}
  </>;

  // Your turn to draw: the pad (anchored in front of you), plus Leave / ? / Leaders on it.
  if (amDrawer && g!.phase === 'drawing') {
    return (
      <VRDrawPad worldId={worldId} tableId={table.id} word={priv?.word ?? null} deadline={phaseDeadline(st)}>
        {topBar(0.35 + 0.065)}
        {overlays}
      </VRDrawPad>
    );
  }

  let H = 0.4;
  let body: React.ReactNode = null;
  const status = (y: number, text: string, color = colors.text, size = 0.022) =>
    <Text raycast={noRaycast} position={[0, y, 0.004]} fontSize={size} color={color} maxWidth={W - 0.06} textAlign="center">{text}</Text>;

  if (st.phase === 'lobby') {
    H = 0.48;
    const o: DrawOptions = { ...DEFAULT_OPTIONS, ...(st.options ?? {}) };
    const set = (patch: Partial<DrawOptions>) => void tableAction(pid, 'configure', { options: patch });
    const n = st.seats.filter(Boolean).length;
    const ready = n >= def.minPlayers;
    body = <>
      {status(H / 2 - 0.1, `${n} joined · needs ${def.minPlayers}–${def.maxPlayers}`, colors.text, 0.019)}
      <OptionButtons label="Rounds" y={0.07} values={ROUND_OPTIONS} current={o.rounds as 2 | 3} fmt={String} onPick={(rounds) => set({ rounds })} disabled={entry.busy} />
      <OptionButtons label="Draw time" y={0.005} values={DRAW_SECONDS} current={o.drawSec as 60 | 80 | 100} fmt={(s) => `${s}s`} onPick={(drawSec) => set({ drawSec })} disabled={entry.busy} />
      <OptionButtons label="Words" y={-0.06} values={DIFFICULTIES} current={o.difficulty} fmt={(d) => d[0].toUpperCase() + d.slice(1)} onPick={(difficulty) => set({ difficulty })} disabled={entry.busy} />
      <VRBtn name="vr-game-Start" label={ready ? 'Start' : `Waiting for players (${n}/${def.minPlayers})`} x={0} y={-0.155} w={0.4} h={0.062}
        active={ready} disabled={!ready || entry.busy} onPress={() => void tableAction(pid, 'start')} />
    </>;
  } else if (g && st.phase === 'over') {
    H = 0.46;
    const rows = standings(st).slice(0, 6);
    const win = g.winners.includes(seat);
    body = <>
      {status(H / 2 - 0.1, win ? 'You win!' : `${g.winners.map((s) => nameAt(st, s)).join(' & ')} win${g.winners.length > 1 ? '' : 's'}`, colors.teal, 0.026)}
      {rows.map((r, i) => (
        <Text key={r.seat} raycast={noRaycast} position={[0, H / 2 - 0.15 - i * 0.032, 0.004]} fontSize={0.017} color={r.seat === seat ? colors.teal : colors.text}>
          {`${i + 1}. ${r.name.slice(0, 18)} — ${r.score}`}
        </Text>
      ))}
      <VRBtn label="Rematch" x={0} y={-H / 2 + 0.05} w={0.3} h={0.06} active disabled={entry.busy} onPress={() => void tableAction(pid, 'rematch')} />
    </>;
  } else if (g && g.phase === 'choosing' && amDrawer) {
    H = 0.36;
    body = <>
      {status(H / 2 - 0.1, 'Your turn! Pick a word to draw', colors.teal)}
      {(priv?.choices ?? []).map((w, i) => (
        <VRBtn key={w} name={`vr-draw-choice-${i}`} label={w} x={-0.22 + i * 0.22} y={-0.02} w={0.2} h={0.09} fontSize={0.02} disabled={entry.busy}
          onPress={() => void tableAction(pid, 'move', { move: { type: 'choose', index: i } })} />
      ))}
      {status(-0.11, 'Then draw it on the pad that appears in front of you', colors.muted, 0.014)}
    </>;
  } else if (g && g.phase === 'drawing') {
    H = canGuess ? 0.66 : 0.4;
    const feed = g.chat.slice(-4);
    const top = H / 2;
    body = <>
      <Text raycast={noRaycast} position={[0, top - 0.11, 0.004]} fontSize={0.034} letterSpacing={0.04} color={colors.text}>{hintText(g.hint)}</Text>
      {!canGuess && status(top - 0.16, myStatus(st, seat, priv), priv?.guessed ? '#7ef0b0' : colors.muted, 0.017)}
      {feed.map((l, i) => (
        <Text key={l.n} raycast={noRaycast} position={[-W / 2 + 0.03, top - 0.17 - i * 0.026 - (canGuess ? 0 : 0.03), 0.004]} anchorX="left" fontSize={0.0145}
          maxWidth={W - 0.06} color={l.kind === 'correct' ? '#7ef0b0' : l.kind === 'sys' ? '#ffd27a' : colors.text}>
          {chatLineText(l).slice(0, 60)}
        </Text>
      ))}
      {canGuess && <>
        <group position={[0, top - 0.305, 0]}>
          <Surface width={W - 0.06} height={0.05} color={colors.card} r={0.01} z={0.002} />
          <Text raycast={noRaycast} position={[-W / 2 + 0.045, 0, 0.006]} anchorX="left" fontSize={0.02} color={typed ? colors.text : colors.muted}>
            {typed ? `${typed}▏` : 'Type your guess…'}
          </Text>
          {close && <Text raycast={noRaycast} position={[W / 2 - 0.045, 0, 0.006]} anchorX="right" fontSize={0.016} color="#ffd27a">so close!</Text>}
        </group>
        <GuessKeyboard y={top - 0.37} onKey={(c) => setTyped((v) => (v + c).slice(0, GUESS_MAX_LEN))} onBack={() => setTyped((v) => v.slice(0, -1))}
          onSend={send} canSend={!!typed.trim() && !entry.busy} />
      </>}
    </>;
  } else if (g) {
    H = 0.34;
    const reveal = g.phase === 'reveal';
    body = <>
      {status(H / 2 - 0.11, reveal ? (g.reveal?.word ? `The word was "${g.reveal.word}"` : 'Turn skipped') : myStatus(st, seat, priv), reveal ? colors.teal : colors.text, 0.024)}
      {reveal && g.turnPts[seat] > 0 && status(H / 2 - 0.16, `You +${g.turnPts[seat]}`, '#7ef0b0', 0.019)}
      {status(-H / 2 + 0.06, `You: ${g.scores[seat] ?? 0} points · leader: ${standings(st)[0]?.name ?? '—'}`, colors.muted, 0.015)}
    </>;
  }

  return (
    <FloatingPanelMount distance={0.55} drop={0.36}>
      <Surface width={W} height={H} color={colors.panel} />
      <Text raycast={noRaycast} position={[-W / 2 + 0.025, H / 2 - 0.035, 0.004]} anchorX="left" fontSize={0.016} color={colors.muted}>
        {g && playing ? `${def.glyph} Round ${g.round}/${g.rounds}` : `${def.glyph} ${def.label}`}
      </Text>
      {playing && <Countdown deadline={phaseDeadline(st)} x={W / 2 - 0.3} y={H / 2 - 0.035} size={0.018} />}
      {topBar(H / 2)}
      <group>{body}</group>
      {entry.error && <Text raycast={noRaycast} position={[0, -H / 2 + 0.015, 0.004]} fontSize={0.013} color="#ff9b9b">{entry.error}</Text>}
      {overlays}
    </FloatingPanelMount>
  );
}

export default DrawingVRPanel;
